import React, { useEffect } from 'react';
import { MdCheck, MdClose } from 'react-icons/md';
import { useAppDispatch, useAppSelector, useTranslate } from '@src/core/hooks';
import { changePassword, errorsSelector, resetPasswordSelector } from '@src/features/auth';
import { Controller, FieldValues, useForm } from 'react-hook-form';
import Button from '@src/components/ui2/Button';
import { Actions, SubTitle } from './styles';
import { convertGeneralContextErrorToHookForm } from '@src/common';
import Input from '@src/components/ui2/Input';
import Loading from '@src/components/ui2/Loading';
import { schema } from './schema';
import { zodResolver } from '@hookform/resolvers/zod';

type ChangePasswordFormProps = {
  email: string;
  onCancel?: () => void;
};

const ChangePasswordForm = (props: ChangePasswordFormProps) => {
  const { email, onCancel = () => {} } = props;

  const dispatch = useAppDispatch();
  const { i18n } = useTranslate();

  const contextErrors = useAppSelector(errorsSelector);
  const { loading, code } = useAppSelector(resetPasswordSelector);

  const { handleSubmit, control, formState, setError } = useForm({
    mode: 'onSubmit',
    reValidateMode: 'onBlur',
    resolver: zodResolver(schema(i18n)),
    defaultValues: {
      newPassword: '',
      newPasswordConfirmation: '',
    },
  });
  const { errors } = formState;

  const submit = (payload: FieldValues) => {
    dispatch(
      changePassword({
        code: code!,
        email: email,
        password: payload['newPassword'],
      }),
    );
  };

  useEffect(() => {
    convertGeneralContextErrorToHookForm('newPassword', setError, contextErrors, 'password_recover');
  }, [contextErrors]);

  return (
    <form onSubmit={handleSubmit(submit)}>
      {loading ? (
        <Loading />
      ) : (
        <>
          <SubTitle>{i18n('direct_link_change_password_form_title')}</SubTitle>

          <Controller
            name='newPassword'
            control={control}
            render={({ field: { onChange, value, name, ...rest } }) => (
              <Input
                type='password'
                name={name}
                label={i18n('direct_link_change_password_form_password_label')}
                value={value}
                onChange={onChange}
                error={!!errors[name]}
                helperText={errors[name]?.message as string}
                autoComplete='new-password'
                {...rest}
              />
            )}
          />

          <Controller
            name='newPasswordConfirmation'
            control={control}
            render={({ field: { onChange, value, name, ...rest } }) => (
              <Input
                type='password'
                name={name}
                label={i18n('direct_link_change_password_form_password_confirmation_label')}
                value={value}
                onChange={onChange}
                error={!!errors[name]}
                helperText={errors[name]?.message as string}
                autoComplete='new-password'
                {...rest}
              />
            )}
          />

          <Actions>
            <Button outlined onClick={onCancel}>
              <MdClose />
              {i18n('direct_link_change_password_form_cancel')}
            </Button>

            <Button type='submit'>
              <MdCheck />
              {i18n('direct_link_change_password_form_confirm')}
            </Button>
          </Actions>
        </>
      )}
    </form>
  );
};

export default ChangePasswordForm;
